import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

interface StyleConfig {
  primaryColor?: string;
  backgroundColor?: string;
  textColor?: string;
  labelColor?: string;
  borderColor?: string;
  borderRadius?: number;
  fontFamily?: string;
  fontSize?: number;
  buttonTextColor?: string;
  [key: string]: unknown;
}

const FONT_OPTIONS = [
  'Inter, system-ui, sans-serif',
  'Roboto, sans-serif',
  'Poppins, sans-serif',
  'Manrope, sans-serif',
  '"IBM Plex Sans", sans-serif',
  'Georgia, serif',
  'system-ui',
];

@Component({
  selector: 'app-style-config-editor',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './style-config-editor.component.html',
})
export class StyleConfigEditorComponent implements OnChanges {
  @Input() value: string | null = null;
  @Output() valueChange = new EventEmitter<string>();

  simple: StyleConfig = this.defaults();
  fontOptions = FONT_OPTIONS;
  readonly supportsEyeDropper = 'EyeDropper' in window;
  pickingKey: string | null = null;

  readonly colorEntries: { key: string; label: string }[] = [
    { key: 'primaryColor',    label: 'Color de marca' },
    { key: 'buttonTextColor', label: 'Texto del botón' },
    { key: 'backgroundColor', label: 'Fondo del form' },
    { key: 'textColor',       label: 'Color del texto' },
    { key: 'labelColor',      label: 'Color etiquetas' },
    { key: 'borderColor',     label: 'Color del borde' },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) this.hydrateFromValue(this.value);
  }

  private defaults(): StyleConfig {
    return {
      primaryColor: '#3B82F6',
      backgroundColor: '#FFFFFF',
      textColor: '#111827',
      labelColor: '#374151',
      borderColor: '#E5E7EB',
      borderRadius: 8,
      fontFamily: FONT_OPTIONS[0],
      fontSize: 14,
      buttonTextColor: '#FFFFFF',
    };
  }

  private hydrateFromValue(raw: string | null): void {
    if (!raw?.trim()) {
      this.simple = this.defaults();
      return;
    }
    try {
      const parsed = JSON.parse(raw) as StyleConfig;
      this.simple = { ...this.defaults(), ...parsed };
    } catch {
      // ignore invalid JSON, keep defaults
    }
  }

  onSimpleChange(): void {
    const out: Record<string, unknown> = {};
    Object.entries(this.simple).forEach(([k, v]) => {
      if (v !== '' && v != null) out[k] = v;
    });
    this.valueChange.emit(Object.keys(out).length ? JSON.stringify(out) : '');
  }

  resetToDefaults(): void {
    this.simple = this.defaults();
    this.onSimpleChange();
  }

  async openEyeDropper(key: string): Promise<void> {
    if (!this.supportsEyeDropper) return;
    this.pickingKey = key;
    try {
      // EyeDropper is not yet in lib.dom.d.ts; cast through any
      const dropper = new (window as any).EyeDropper();
      const result: { sRGBHex: string } = await dropper.open();
      (this.simple as Record<string, unknown>)[key] = result.sRGBHex;
      this.onSimpleChange();
    } catch {
      // user pressed Escape or cancelled — do nothing
    } finally {
      this.pickingKey = null;
    }
  }
}
